import { useEffect, useState } from "react";
import { Calendar, Clock } from "lucide-react";
import { useTranslation } from "@/i18n/use-translation";

/**
 * A date/time field that takes typed text as readily as a picked value. Extracted from
 * FilterPopover (POS2-1068) so the dashboard's Custom range can use it too: its native
 * `<input type="date">` was narrow enough that the browser's own calendar indicator sat on
 * top of the year. Here the icon is positioned into reserved `pr-8` space, so it cannot
 * overlap the digits at any width, and the native picker hides transparently behind it.
 */
export function InlineDateField({
  label,
  kind,
  placeholder,
  value,
  min,
  onCommit,
  isValid: validate,
  testId,
}: {
  label: string;
  kind: "date" | "time";
  placeholder: string;
  // Canonical value from the draft (e.g. "2026-06-01" / "06:34" / "").
  value: string;
  /** Floor for the native picker, canonical "yyyy-MM-dd" (POS2-1279). The OS calendar greys out
   * everything before it, so the picker cannot produce a date the parent would then refuse.
   * Typing is bounded by the parent, which owns the Apply button and the reason beside it. */
  min?: string;
  // Called with the raw text (typing) or the picker's value; the parent parses
  // + stores only when it's valid, so partial/invalid input never clobbers.
  onCommit: (raw: string) => void;
  isValid: (raw: string) => boolean;
  testId: string;
}) {
  const { t } = useTranslation();
  // Two ways to enter a value: type freely in the text input, or click the
  // icon to open the browser's native segmented picker. Local text state lets
  // partial input show while typing; it resyncs to the canonical value only
  // while unfocused (open-seed, Clear link, picker change) so it doesn't fight
  // the user mid-edit — and that resync is what normalises "20260707" on blur.
  const [text, setText] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(value);
  }, [value, focused]);

  const invalid = text !== "" && !validate(text);
  const Icon = kind === "date" ? Calendar : Clock;
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-400">
      {label}
      <div className="relative">
        <input
          type="text"
          placeholder={placeholder}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            onCommit(e.target.value);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          // The placeholder is the page's default value, not a format hint, so it reads as a
          // value (gray-900) — an empty field means "the default", never "unbounded" (POS2-1049).
          className={`w-full rounded-md border bg-white px-2 py-1.5 pr-8 text-sm text-gray-900 placeholder:text-gray-900 outline-none focus:ring-1 ${
            invalid
              ? "border-red-300 focus:border-red-300 focus:ring-red-500"
              : "border-gray-200 focus:border-gray-300 focus:ring-blue-500"
          }`}
          data-testid={testId}
        />
        <Icon
          className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-gray-400"
          aria-hidden
        />
        {/* Transparent native input over the icon — clicking it opens the
            browser's segmented date/time picker (anchored here). Its value
            feeds the same draft as the text input. */}
        <input
          type={kind}
          value={value}
          min={min}
          onChange={(e) => onCommit(e.target.value)}
          onClick={(e) => e.currentTarget.showPicker?.()}
          tabIndex={-1}
          aria-label={t("waste.datePicker", { label })}
          className="absolute inset-y-0 right-0 w-8 cursor-pointer opacity-0"
          data-testid={`${testId}-picker`}
        />
      </div>
    </label>
  );
}
